import { useCallback, useEffect, useState } from "react";
import {
  Webhook,
  ShieldCheck,
  KeyRound,
  Lock,
  Copy,
  CheckCircle2,
  Eye,
  EyeOff,
  Code2,
  ChevronDown,
  ChevronUp,
  Terminal,
  FileJson,
  Hash,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getSupabaseClient } from "../providers/supabase/supabase";

interface WebhookConfig {
  api_key: string | null;
  secret: string | null;
  url_token?: string | null;
  url_with_token?: string | null;
  sample_body: string;
  sample_hash: string | null;
  warnings: string[];
}

interface CredentialFieldProps {
  id: string;
  label: string;
  tag: string;
  icon: React.ReactNode;
  value: string;
  hint: React.ReactNode;
  secret?: boolean;
  unavailableText?: string;
  copiedKey: string | null;
  onCopy: (text: string, id: string) => void;
}

const CredentialField = ({
  id,
  label,
  tag,
  icon,
  value,
  hint,
  secret = false,
  unavailableText,
  copiedKey,
  onCopy,
}: CredentialFieldProps) => {
  const [visible, setVisible] = useState(false);
  const available = value !== "";
  const copied = copiedKey === id;

  return (
    <div className="space-y-1.5 p-3.5 rounded-lg border border-border/60 bg-muted/30">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold flex items-center gap-1.5">
          {icon}
          {label}
        </Label>
        <span className="text-[10px] text-muted-foreground font-mono">
          {tag}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Input
            readOnly
            type={secret && !visible ? "password" : "text"}
            value={available ? value : (unavailableText ?? "Indisponível")}
            className={`h-8 text-xs font-mono bg-background text-foreground select-all border-border/80 ${secret ? "pr-8" : ""}`}
          />
          {secret && available && (
            <button
              type="button"
              onClick={() => setVisible(!visible)}
              className="absolute right-2 top-2 text-muted-foreground hover:text-foreground cursor-pointer"
              title={visible ? "Ocultar" : "Mostrar"}
            >
              {visible ? (
                <EyeOff className="w-3.5 h-3.5" />
              ) : (
                <Eye className="w-3.5 h-3.5" />
              )}
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!available}
          onClick={() => onCopy(value, id)}
          className="h-8 px-2.5 gap-1 shrink-0 text-xs border-border/80 hover:bg-muted cursor-pointer"
        >
          {copied ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-[11px] text-emerald-600 font-medium">
                Copiado
              </span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span className="text-[11px]">Copiar</span>
            </>
          )}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
};

export const SuperAdminWebhookCard = () => {
  const [config, setConfig] = useState<WebhookConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showDocs, setShowDocs] = useState(false);

  const webhookUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/webhook`
      : "/api/webhook";

  // As credenciais vêm do servidor, só para um Superadmin autenticado.
  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getSupabaseClient().auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error("Sessão do Superadmin não encontrada.");

      const res = await fetch("/api/webhook-config", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          json?.error?.message || `Falha ao carregar (HTTP ${res.status}).`,
        );
      }
      setConfig(json as WebhookConfig);
    } catch (err: any) {
      setConfig(null);
      setError(
        err?.message || "Não foi possível carregar as credenciais do webhook.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey((prev) => (prev === id ? null : prev)), 2500);
  };

  const prettySample = config
    ? JSON.stringify(JSON.parse(config.sample_body), null, 2)
    : "";
  const sampleCurl = config
    ? `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${config.api_key ?? "<DOTUS_API_KEY>"}" \\
  -H "x-webhook-signature: ${config.sample_hash ?? "<HMAC-SHA256 do corpo>"}" \\
  -d '${config.sample_body}'`
    : "";

  return (
    <Card className="border-border/70 bg-card/80 backdrop-blur-sm shadow-sm overflow-hidden">
      <CardHeader className="p-5 pb-4 border-b border-border/50 bg-muted/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20 shadow-xs">
              <Webhook className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-bold tracking-tight">
                  Webhook Global & Chave de Acesso
                </CardTitle>
                <Badge
                  variant="outline"
                  className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px] font-semibold"
                >
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  Superadmin Exclusivo
                </Badge>
              </div>
              <CardDescription className="text-xs text-muted-foreground mt-0.5">
                Recebe o retorno das ligações e cria a atividade concluída no
                negócio vinculado ao ID da chamada.
              </CardDescription>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-center">
            <Badge
              variant="secondary"
              className="font-mono text-[10px] px-2 py-0.5"
            >
              POST
            </Badge>
            <Badge
              variant="outline"
              className="text-[10px] bg-primary/5 text-primary border-primary/20"
            >
              <Hash className="w-2.5 h-2.5 mr-1" />
              HMAC-SHA256 obrigatório
            </Badge>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={loadConfig}
              disabled={loading}
              title="Recarregar credenciais"
              className="h-7 w-7 p-0 cursor-pointer"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-5">
        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {config?.warnings?.map((w) => (
          <div
            key={w}
            className="p-3 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{w}</span>
          </div>
        ))}

        {/* URL Direta API4COM com Token SHA-256 */}
        <div className="p-4 rounded-xl border border-primary/30 bg-primary/5 space-y-2.5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-primary text-primary-foreground font-semibold text-[11px] px-2 py-0.5">
                API4COM & Telefonia
              </Badge>
              <span className="text-xs font-semibold text-foreground">
                URL do Webhook com Token SHA-256 Integrado
              </span>
            </div>
            <Badge
              variant="outline"
              className="text-[10px] text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10 w-fit"
            >
              Autenticação Direta na URL
            </Badge>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Esta URL combina o hash de segurança e a chave do Superadmin em um
            hash SHA-256 exclusivo. Basta cadastrá-la diretamente na{" "}
            <b>API4COM</b>: as ligações são recebidas e autenticadas sem
            precisar de headers adicionais.
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={
                  config?.url_token
                    ? typeof window !== "undefined"
                      ? `${window.location.origin}/api/webhook?token=${config.url_token}`
                      : `/api/webhook?token=${config.url_token}`
                    : loading
                      ? "Gerando URL com hash..."
                      : webhookUrl
                }
                className="font-mono text-xs bg-background/90 h-9 select-all"
              />
              <Button
                type="button"
                size="sm"
                className="gap-1.5 h-9 shrink-0 cursor-pointer"
                disabled={loading || !config?.url_token}
                onClick={() =>
                  handleCopy(
                    typeof window !== "undefined" && config?.url_token
                      ? `${window.location.origin}/api/webhook?token=${config.url_token}`
                      : `/api/webhook?token=${config?.url_token || ""}`,
                    "direct_url_param",
                  )
                }
              >
                {copiedKey === "direct_url_param" ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copiar URL</span>
                  </>
                )}
              </Button>
            </div>
            {typeof window !== "undefined" &&
              window.location.hostname === "localhost" && (
                <div className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1.5 pt-0.5">
                  <AlertCircle className="w-3 h-3 shrink-0" />
                  <span>
                    <b>Atenção em desenvolvimento local:</b> A API4COM roda na
                    nuvem e não alcança <code>localhost</code> diretamente. Para
                    receber webhooks reais da API4COM no seu computador, utilize
                    um túnel público (ex: <code>ngrok http 5173</code>)
                    substituindo <code>http://localhost:5173</code> pela URL do
                    ngrok.
                  </span>
                </div>
              )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CredentialField
            id="url"
            label="URL do Webhook"
            tag="Rota Geral"
            icon={<Webhook className="w-3.5 h-3.5 text-primary" />}
            value={webhookUrl}
            hint="O corpo precisa trazer o id da ligação e o hash."
            copiedKey={copiedKey}
            onCopy={handleCopy}
          />
          <CredentialField
            id="key"
            label="Chave da API (Superadmin)"
            tag="x-api-key"
            icon={<KeyRound className="w-3.5 h-3.5 text-amber-500" />}
            value={config?.api_key ?? ""}
            secret
            unavailableText={loading ? "Carregando..." : "Não configurada"}
            hint={
              <>
                Header{" "}
                <code className="text-[10px] px-1 py-0.5 rounded bg-muted font-mono">
                  x-api-key
                </code>{" "}
                ou Bearer token.
              </>
            }
            copiedKey={copiedKey}
            onCopy={handleCopy}
          />
          <CredentialField
            id="secret"
            label="Segredo do Hash (Secret)"
            tag="HMAC SHA256"
            icon={<Lock className="w-3.5 h-3.5 text-indigo-500" />}
            value={config?.secret ?? ""}
            secret
            unavailableText={loading ? "Carregando..." : "Não configurado"}
            hint={
              <>
                Assina o corpo em{" "}
                <code className="text-[10px] px-1 py-0.5 rounded bg-muted font-mono">
                  x-webhook-signature
                </code>{" "}
                ou no campo{" "}
                <code className="text-[10px] px-1 py-0.5 rounded bg-muted font-mono">
                  hash
                </code>
                .
              </>
            }
            copiedKey={copiedKey}
            onCopy={handleCopy}
          />
        </div>

        {config && (
          <div className="pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowDocs(!showDocs)}
              className="text-xs text-primary hover:text-primary/80 gap-1.5 p-0 h-auto cursor-pointer"
            >
              <Code2 className="w-3.5 h-3.5" />
              <span>
                {showDocs
                  ? "Ocultar exemplos de integração e cURL"
                  : "Ver exemplos de payload e cURL"}
              </span>
              {showDocs ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </Button>

            {showDocs && (
              <div className="mt-3.5 grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in-50 duration-200">
                <div className="p-3.5 rounded-lg border border-border/60 bg-muted/20 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold">
                      <FileJson className="w-3.5 h-3.5 text-primary" />
                      Body JSON Esperado
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(config.sample_body, "json")}
                      className="h-6 px-2 text-[11px] gap-1 cursor-pointer"
                    >
                      {copiedKey === "json" ? (
                        <>
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          <span className="text-emerald-600">Copiado</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copiar JSON</span>
                        </>
                      )}
                    </Button>
                  </div>
                  <pre className="text-[11px] font-mono p-2.5 rounded bg-background/80 border border-border/60 overflow-x-auto text-muted-foreground leading-relaxed">
                    {prettySample}
                  </pre>
                  <p className="text-[10px] text-muted-foreground">
                    Datas sem fuso são lidas como horário de Brasília (-03:00).
                  </p>
                </div>

                <div className="p-3.5 rounded-lg border border-border/60 bg-muted/20 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold">
                      <Terminal className="w-3.5 h-3.5 text-primary" />
                      Comando cURL de Exemplo
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(sampleCurl, "curl")}
                      className="h-6 px-2 text-[11px] gap-1 cursor-pointer"
                    >
                      {copiedKey === "curl" ? (
                        <>
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          <span className="text-emerald-600">Copiado</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copiar cURL</span>
                        </>
                      )}
                    </Button>
                  </div>
                  <pre className="text-[11px] font-mono p-2.5 rounded bg-background/80 border border-border/60 overflow-x-auto text-muted-foreground leading-relaxed whitespace-pre-wrap break-all">
                    {sampleCurl}
                  </pre>
                  <p className="text-[10px] text-muted-foreground">
                    O hash é o HMAC-SHA256 desse corpo exato (JSON compacto),
                    calculado com o segredo acima.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
